import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Drawer,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  ListItem,
  Progress,
  Select,
  Spinner,
  Text,
  Tooltip,
  UnorderedList,
  useToast,
  VStack,
} from "@chakra-ui/react";
import ReactJson from "react-json-view";
import { useAuthenticatedRequest } from "../../hooks/useAuthenticatedRequest";
import { usePartyNames } from "../../hooks/usePartyNames";
import { unwrapApiResponse } from "../../utils/unwrapApiResponse";
import { showApiErrorToast } from "../../utils/showApiToast";
import { STATE_COLOURS, isActive } from "./DocumentCodifyRuns";
import { getPlacementLines, getQuoteRequestsForPlacement, getSignedLineAdvicesForPlacement } from "../../services/PlacingAPI";

const PLACING_BASE = "https://placing.poc.mrc.ai/api";
const DOCUMENTS_BASE = "https://documents.poc.mrc.ai/api";
const CODIFY_BASE = "https://codify.poc.mrc.ai/api";
const POLL_MS = 2000;
export const TRIGGER_LABELS = { submission: "Submission", quote_request: "Quote request", line_request: "Line request", signed_line_advice: "Signed line advice" };
const TRIGGER_PLURALS = { submission: "Submissions", quote_request: "Quote requests", line_request: "Line requests", signed_line_advice: "Signed line advices" };

const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
const raw = (v) => v?.$date ?? v;
const time = (v) => (raw(v) ? new Date(raw(v)).getTime() : 0);
const when = (v) => (raw(v) ? new Date(raw(v)).toLocaleString([], { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const parse = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch {
    return undefined;
  }
};

/**
 * The latest run of a setting for a message (trigger + triggerId), or of
 * any message when none is given.
 */
export const latestRun = (setting, message) =>
  [...(setting?.runs || [])]
    .filter((r) => !message || (r.trigger === message.trigger && r.triggerId === message.triggerId))
    .sort((a, b) => time(b.startedDate) - time(a.startedDate))[0] || null;

/** The latest run for the message, else the latest run of all (when fallback is on). */
const pickRun = (settings, message, fallback = true) => {
  const pick = (m) =>
    (settings || [])
      .map((s) => ({ setting: s, run: latestRun(s, m) }))
      .filter((x) => x.run)
      .sort((a, b) => time(b.run.startedDate) - time(a.run.startedDate))[0] || null;
  return (message && pick(message)) || (fallback || !message ? pick(null) : null);
};

/**
 * A setting's runs as one entry per codification: messages sent together
 * share a run (same codificationId), shown once. Newest first:
 * [{ codificationId, trigger, startedDate, entries: [run] }].
 */
export const groupRuns = (runs) => {
  const groups = [];
  [...(runs || [])]
    .sort((a, b) => time(b.startedDate) - time(a.startedDate))
    .forEach((r) => {
      const g = r.codificationId && groups.find((x) => x.codificationId === r.codificationId);
      if (g) g.entries.push(r);
      else groups.push({ codificationId: r.codificationId, trigger: r.trigger, startedDate: r.startedDate, entries: [r] });
    });
  return groups;
};

export const runLabel = (group) =>
  group.entries.length > 1
    ? `${TRIGGER_PLURALS[group.trigger] || group.trigger} ×${group.entries.length}`
    : TRIGGER_LABELS[group.trigger] || group.trigger || "Manual";

const JsonView = ({ value }) => {
  const parsed = parse(value);
  if (parsed === null) return <Text fontSize="xs" color="gray.500">Nothing yet.</Text>;
  if (parsed === undefined || typeof parsed !== "object") return <Text fontSize="xs" whiteSpace="pre-wrap">{String(value)}</Text>;
  return <ReactJson src={parsed} name={false} collapsed={2} displayDataTypes={false} displayObjectSize={false} enableClipboard indentWidth={2} style={{ fontSize: "11px", backgroundColor: "white" }} />;
};

/** What a run produced: its contract data when valid against a schema, otherwise its output. */
export const runData = (codification) =>
  codification?.contractData
    ? { value: codification.contractData.body, label: `Contract data · ${codification.contractData.schema || ""}${codification.contractData.schemaVersion ? ` v${codification.contractData.schemaVersion}` : ""}` }
    : codification?.output
    ? { value: codification.output, label: "Output (not validated against a contract schema)" }
    : null;

/** Read-only view of a run's JSON, with copy and download. */
export const JsonViewerModal = ({ viewer, onClose }) => {
  const toast = useToast();
  const parsed = parse(viewer?.value);
  const text = parsed && typeof parsed === "object" ? JSON.stringify(parsed, null, 2) : String(viewer?.value ?? "");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "JSON copied", status: "success", duration: 1500 });
    } catch {
      toast({ title: "Couldn't copy to the clipboard", status: "error" });
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${viewer?.fileName || "codified-data"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Modal isOpen={!!viewer} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pr={12}>
          {viewer?.title || "Codified data"}
          <Text fontSize="sm" fontWeight="normal" color="gray.500">{viewer?.subtitle}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box borderWidth="1px" borderRadius="md" p={3} bg="white" minH="200px">
            {parsed && typeof parsed === "object" ? (
              <ReactJson src={parsed} name={false} collapsed={3} displayDataTypes={false} displayObjectSize enableClipboard
                indentWidth={2} style={{ fontSize: "12px" }} />
            ) : (
              <Text fontSize="sm" fontFamily="mono" whiteSpace="pre-wrap">{text || "Nothing yet."}</Text>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Text fontSize="xs" color="gray.500" mr="auto">Read only</Text>
          <Button size="sm" variant="ghost" mr={2} onClick={copy} isDisabled={!text}>Copy JSON</Button>
          <Button size="sm" onClick={download} isDisabled={!text}>Download .json</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/** One run (its messages, state, progress); expands to recipients, problems and contract data. */
const RunRow = ({ group, codification, onAgain, isBusy, onView, recipientNames, onExpand }) => {
  const run = group.entries[0];
  const [open, setOpen] = useState(false);
  const toggle = () => {
    if (!open) onExpand?.(group);
    setOpen(!open);
  };
  const state = codification?.state || (run.error ? "failed" : run.codificationId ? "…" : "not started");
  const total = codification?.rowsTotal || 0;
  const error = codification?.error || run.error;
  return (
    <Box borderWidth="1px" borderRadius="md" bg="white">
      <HStack as="button" type="button" w="100%" textAlign="left" px={3} py={2} spacing={3} onClick={toggle}>
        <Box flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="medium">{runLabel(group)} · {when(run.startedDate)}</Text>
          {codification?.specificationName && <Text fontSize="xs" color="gray.500" noOfLines={1}>{codification.specificationName}</Text>}
        </Box>
        {total > 0 && <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">{codification.rowsDone || 0}/{total} rows{codification.rowsFailed ? ` · ${codification.rowsFailed} failed` : ""}</Text>}
        <Badge colorScheme={STATE_COLOURS[state] || "gray"} textTransform="none">{state}</Badge>
        {isActive(codification) && <Spinner size="xs" color="blue.500" />}
      </HStack>
      {total > 0 && isActive(codification) && <Progress value={((codification.rowsDone || 0) / total) * 100} size="xs" mx={3} mb={2} borderRadius="full" />}
      <Collapse in={open} animateOpacity>
        <VStack align="stretch" spacing={3} px={3} pb={3}>
          {group.entries.length > 1 && (
            <Box>
              <Text fontSize="xs" fontWeight="semibold">Sent together, codified once</Text>
              <UnorderedList fontSize="xs" color="gray.700">
                {group.entries.map((e) => (
                  <ListItem key={e.triggerId}>
                    {TRIGGER_LABELS[e.trigger] || e.trigger} to {recipientNames?.[e.triggerId] || "…"}
                  </ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}
          {error && (
            <Alert status="error" borderRadius="md" py={2}>
              <AlertIcon />
              <AlertDescription fontSize="xs">{error}</AlertDescription>
            </Alert>
          )}
          {(codification?.validationErrors || []).length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="orange.700">Doesn't match the contract schema</Text>
              <UnorderedList fontSize="xs" color="orange.800">
                {codification.validationErrors.map((e, i) => <ListItem key={i}>{e}</ListItem>)}
              </UnorderedList>
            </Box>
          )}
          <Box>
            <Text fontSize="xs" fontWeight="semibold" mb={1}>
              {codification?.contractData ? `Contract data · ${codification.contractData.schema || ""} v${codification.contractData.schemaVersion || ""}` : "Output"}
            </Text>
            <Box borderWidth="1px" borderRadius="md" p={2} maxH="360px" overflowY="auto">
              <JsonView value={codification?.contractData ? codification.contractData.body : codification?.output} />
            </Box>
          </Box>
          <HStack justify="flex-end">
            {runData(codification) && (
              <Button size="xs" variant="outline" onClick={() => onView(run, codification)}>Open JSON</Button>
            )}
            <Button size="xs" onClick={() => onAgain(run)} isLoading={isBusy} isDisabled={isActive(codification)}>Codify again</Button>
          </HStack>
        </VStack>
      </Collapse>
    </Box>
  );
};

/**
 * The Codify chip on the placing bar and its panel. It shows only the
 * user's own parties' Codify settings and runs (the API returns no
 * others). message is the current message ({ trigger, triggerId }); the
 * chip shows its latest run. refreshKey reloads the settings (e.g. after
 * the submission is shared or opened).
 */
const PlacementCodify = ({ placementId, message, awaitMessage, refreshKey }) => {
  const toast = useToast();
  const { get, post, put } = useAuthenticatedRequest();
  const request = useRef({ get, post, put });
  request.current = { get, post, put };

  const [settings, setSettings] = useState(null);
  const [codifications, setCodifications] = useState({}); // codificationId -> codification
  const [open, setOpen] = useState(false);
  const [specs, setSpecs] = useState(null);
  const [busy, setBusy] = useState(null);
  const [viewer, setViewer] = useState(null); // { title, subtitle, value, fileName }
  const pollTimer = useRef(null);
  const settleTries = useRef(0);
  // Who each message went to (triggerId -> partyId), for runs shared by several messages
  const [recipients, setRecipients] = useState({});
  const recipientsLoaded = useRef(false);

  const loadSettings = useCallback(async () => {
    if (!placementId) return;
    try {
      const res = await request.current.get(`${PLACING_BASE}/placements/${encodeURIComponent(placementId)}/codify`);
      setSettings(asList(unwrapApiResponse(res.data)));
    } catch (err) {
      setSettings((prev) => prev || []);
      // Nothing to show is fine; anything else is worth saying
      if (![403, 404].includes(err?.response?.status)) showApiErrorToast(toast, err, { title: "Couldn't load Codify" });
    }
  }, [placementId, toast]);

  useEffect(() => {
    settleTries.current = 0;
    loadSettings();
  }, [loadSettings, refreshKey]);

  // The runs to follow: every run of the user's settings
  const runs = useMemo(() => (settings || []).flatMap((s) => (s.runs || []).map((r) => ({ ...r, partyId: s.partyId }))), [settings]);
  const runIds = runs.map((r) => r.codificationId).filter(Boolean);
  const runKey = runIds.join(",");

  const fetchCodification = useCallback(async (id) => {
    try {
      const res = await request.current.get(`${DOCUMENTS_BASE}/codifications/${encodeURIComponent(id)}`);
      const codification = unwrapApiResponse(res.data);
      setCodifications((prev) => ({ ...prev, [id]: codification }));
      return codification;
    } catch {
      return null;
    }
  }, []);

  // Load each run, then poll the unfinished ones every 2 seconds
  useEffect(() => {
    let stopped = false;
    const tick = async (ids) => {
      const results = await Promise.all(ids.map(fetchCodification));
      const active = ids.filter((id, i) => !results[i] || isActive(results[i]));
      if (!stopped && active.length) pollTimer.current = setTimeout(() => tick(active), POLL_MS);
    };
    if (runIds.length) tick(runIds);
    return () => {
      stopped = true;
      clearTimeout(pollTimer.current);
    };
    // runKey stands for runIds
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey, fetchCodification]);

  // A run starts on the server after sending or opening: check back a few times until it appears
  const awaited = awaitMessage || message;
  const hasCurrentRun = (settings || []).some((s) => latestRun(s, awaited));
  useEffect(() => {
    if (!settings || !awaited || hasCurrentRun || settleTries.current >= 6) return undefined;
    const timer = setTimeout(() => {
      settleTries.current += 1;
      loadSettings();
    }, 3000);
    return () => clearTimeout(timer);
  }, [settings, awaited, hasCurrentRun, loadSettings]);

  // The recipients of the broker's messages, the first time a shared run is opened
  const loadRecipients = async () => {
    if (recipientsLoaded.current) return;
    recipientsLoaded.current = true;
    const map = {};
    const quietly = (p) => p.catch(() => null);
    const [quoteRequests, lines, advices] = await Promise.all([
      quietly(getQuoteRequestsForPlacement(placementId)),
      quietly(getPlacementLines(placementId)),
      quietly(getSignedLineAdvicesForPlacement(placementId)),
    ]);
    (quoteRequests || []).forEach((q) => (map[q._id] = q.receiver?.partyId));
    (lines?.rows || []).forEach((r) => (map[r.lineRequestId] = r.partyId));
    (advices || []).forEach((a) => (map[a._id] = a.receiver?.partyId));
    setRecipients(map);
  };

  const openPanel = () => {
    setOpen(true);
    if (!specs) {
      request.current
        .get(`${CODIFY_BASE}/specifications?page=1&pageSize=200`)
        .then((res) => setSpecs(asList(unwrapApiResponse(res.data))))
        .catch((err) => {
          setSpecs([]);
          showApiErrorToast(toast, err, { title: "Couldn't load the specifications" });
        });
    }
  };

  const change = async (setting, specificationId) => {
    const spec = (specs || []).find((s) => s._id === specificationId);
    setBusy(`spec-${setting.partyId}`);
    try {
      await request.current.put(`${PLACING_BASE}/placements/${encodeURIComponent(placementId)}/codify/${encodeURIComponent(setting.partyId)}`, {
        specificationId,
        specificationName: spec?.name || "",
        rerun: true,
      });
      toast({ title: "Specification changed", description: "Your latest message is being codified again.", status: "success" });
      await loadSettings();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't change the specification" });
    } finally {
      setBusy(null);
    }
  };

  const again = async (setting, run) => {
    setBusy(`run-${setting.partyId}`);
    try {
      await request.current.post(`${PLACING_BASE}/placements/${encodeURIComponent(placementId)}/codify/${encodeURIComponent(setting.partyId)}/run`,
        run?.trigger ? { trigger: run.trigger, triggerId: run.triggerId } : {});
      toast({ title: "Codifying again", status: "success", duration: 2000 });
      await loadSettings();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't codify again" });
    } finally {
      setBusy(null);
    }
  };

  const names = usePartyNames([...(settings || []).map((s) => s.partyId), ...Object.values(recipients)]);
  const recipientNames = Object.fromEntries(Object.entries(recipients).map(([id, partyId]) => [id, names[partyId] || "…"]));

  const view = (run, codification, setting) => {
    const data = runData(codification);
    if (!data) return;
    setViewer({
      title: data.label,
      subtitle: [
        names[setting?.partyId],
        codification.specificationName || setting?.specificationName,
        `${TRIGGER_LABELS[run.trigger] || run.trigger || "Manual"} · ${when(run.startedDate)}`,
        codification.state,
      ].filter(Boolean).join(" · "),
      value: data.value,
      fileName: `${run.trigger || "codify"}-${(run.triggerId || run.codificationId || "").slice(0, 8)}`,
    });
  };

  if (!placementId || settings === null || settings.length === 0) return null;

  // The chip: the run for the message that's open, else the latest run
  const picked = pickRun(settings, message);
  const current = picked?.run;
  const currentState = current ? codifications[current.codificationId]?.state || (current.error ? "failed" : "queued") : null;
  const hasProblem = settings.some((s) => s.problem);
  const chipLabel = current ? currentState : hasProblem ? "needs a specification" : "not run";
  const currentCodification = current && codifications[current.codificationId];
  const currentSetting = picked?.setting;

  return (
    <>
      <Tooltip label="Codify: your own codification of this contract" hasArrow>
        <Button size="xs" variant="outline" onClick={openPanel}
          colorScheme={current ? STATE_COLOURS[currentState] || "gray" : hasProblem ? "orange" : "gray"}
          leftIcon={isActive({ state: currentState }) ? <Spinner size="xs" /> : undefined}>
          Codify · {chipLabel}
        </Button>
      </Tooltip>
      {runData(currentCodification) && !isActive(currentCodification) && (
        <Tooltip label="Open the data codified from this message (read only)" hasArrow>
          <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => view(current, currentCodification, currentSetting)}>
            {"{ }"} View data
          </Button>
        </Tooltip>
      )}

      <Drawer isOpen={open} onClose={() => setOpen(false)} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            Codify
            <Text fontSize="sm" fontWeight="normal" color="gray.500">Only your own setting and runs are shown</Text>
          </DrawerHeader>
          <DrawerBody py={4} bg="gray.50">
            <VStack align="stretch" spacing={5}>
              {settings.map((s) => {
                const sorted = groupRuns(s.runs);
                const specOptions = specs || [];
                return (
                  <Box key={s.partyId}>
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="semibold">{names[s.partyId] || "Your party"}</Text>
                      {s.roleCode && <Badge textTransform="none">{s.roleCode}</Badge>}
                    </HStack>
                    <Box borderWidth="1px" borderRadius="md" bg="white" p={3} mb={3}>
                      <HStack justify="space-between" align="start">
                        <Box>
                          <Text fontSize="xs" color="gray.500">Specification</Text>
                          <Text fontSize="sm" fontWeight="medium">{s.specificationName || "None"}</Text>
                        </Box>
                        {s.specificationId && (
                          <Badge colorScheme={s.source === "chosen" ? "purple" : "gray"} textTransform="none">
                            {s.source === "chosen" ? "chosen" : "product default"}
                          </Badge>
                        )}
                      </HStack>
                      <HStack mt={2}>
                        <Text fontSize="xs" color="gray.600" flexShrink={0}>Change</Text>
                        <Select size="xs" value="" isDisabled={!specs || busy === `spec-${s.partyId}`}
                          onChange={(e) => e.target.value && change(s, e.target.value)}>
                          <option value="">{specs ? "Choose a specification…" : "Loading…"}</option>
                          {specOptions.filter((sp) => sp._id !== s.specificationId).map((sp) => (
                            <option key={sp._id} value={sp._id}>{sp.name || sp._id}{sp.contractSchemaId ? ` · ${sp.contractSchemaId}` : ""}</option>
                          ))}
                        </Select>
                        {busy === `spec-${s.partyId}` && <Spinner size="xs" />}
                      </HStack>
                      {s.problem && (
                        <Alert status="warning" borderRadius="md" mt={3} py={2}>
                          <AlertIcon />
                          <AlertDescription fontSize="xs">{s.problem}</AlertDescription>
                        </Alert>
                      )}
                    </Box>

                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="semibold">Runs</Text>
                      {s.specificationId && (
                        <Button size="xs" variant="outline" onClick={() => again(s, null)} isLoading={busy === `run-${s.partyId}`}>
                          Codify latest message
                        </Button>
                      )}
                    </HStack>
                    {sorted.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">No runs yet.</Text>
                    ) : (
                      <VStack align="stretch" spacing={2}>
                        {sorted.map((g, i) => (
                          <RunRow key={g.codificationId || i} group={g} codification={codifications[g.codificationId]}
                            onAgain={(run) => again(s, run)} isBusy={busy === `run-${s.partyId}`}
                            onView={(run, c) => view(run, c, s)} recipientNames={recipientNames}
                            onExpand={(grp) => grp.entries.length > 1 && loadRecipients()} />
                        ))}
                      </VStack>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <JsonViewerModal viewer={viewer} onClose={() => setViewer(null)} />
    </>
  );
};

export default PlacementCodify;

/**
 * Follows your own run for one message: { loading, setting, run,
 * codification, reload }. With fallback, uses the latest run when the
 * message has none. Polls every 2 seconds while the run is unfinished, and
 * checks back a few times while it hasn't started yet (it starts on the
 * server as the message is sent or opened).
 */
export const useMessageCodification = ({ placementId, message, refreshKey, fallback = false }) => {
  const { get } = useAuthenticatedRequest();
  const request = useRef(get);
  request.current = get;
  const [found, setFound] = useState({ loading: true });
  const messageKey = message ? `${message.trigger}|${message.triggerId}` : "";

  const look = useCallback(async () => {
    if (!placementId) return { loading: false };
    const res = await request.current(`${PLACING_BASE}/placements/${encodeURIComponent(placementId)}/codify`);
    const picked = pickRun(asList(unwrapApiResponse(res.data)), message, fallback);
    if (!picked) return { loading: false };
    let codification = null;
    if (picked.run.codificationId) {
      const c = await request.current(`${DOCUMENTS_BASE}/codifications/${encodeURIComponent(picked.run.codificationId)}`);
      codification = unwrapApiResponse(c.data);
    }
    return { loading: false, ...picked, codification };
  }, [placementId, messageKey, fallback]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let stopped = false;
    let timer = null;
    let waits = 0;
    const tick = async () => {
      let next;
      try {
        next = await look();
      } catch {
        next = { loading: false };
      }
      if (stopped) return;
      setFound(next);
      // Keep following an unfinished run (one with no codification yet hasn't been picked up)
      if (next.run?.codificationId && (!next.codification || isActive(next.codification))) timer = setTimeout(tick, POLL_MS);
      else if (!next.run && waits < 6) {
        waits += 1;
        timer = setTimeout(tick, 3000);
      }
    };
    tick();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [look, refreshKey]);

  return { ...found, reload: async () => setFound(await look()) };
};

const viewerFor = (f, partyName) => {
  const data = runData(f?.codification);
  if (!data) return null;
  return {
    title: data.label,
    subtitle: [partyName, f.codification.specificationName || f.setting?.specificationName,
      `${TRIGGER_LABELS[f.run.trigger] || f.run.trigger} · ${when(f.run.startedDate)}`, f.codification.state].filter(Boolean).join(" · "),
    value: data.value,
    fileName: `${f.run.trigger || "codify"}-${(f.run.triggerId || "").slice(0, 8)}`,
  };
};

/**
 * A button that opens your own codified data for one message (e.g. the
 * submission) read only, for the message screens. It says when there's
 * nothing yet or it's still running.
 */
export const CodifiedDataButton = ({ placementId, message, size = "sm", variant = "outline", refreshKey }) => {
  const toast = useToast();
  const found = useMessageCodification({ placementId, message, refreshKey });
  const [viewer, setViewer] = useState(null);
  const names = usePartyNames(found.setting ? [found.setting.partyId] : []);

  if (!placementId || !message) return null;

  const open = () => {
    const v = viewerFor(found, names[found.setting?.partyId]);
    if (!v) return toast({ title: isActive(found.codification) ? "Still codifying — try again in a moment" : "Nothing codified for this yet", status: "info", duration: 2500 });
    setViewer(v);
  };

  const state = found.codification?.state || (found.run?.error ? "failed" : null);
  const label = found.loading ? "Codified data" : !found.run ? "Codified data · none yet" : isActive(found.codification) ? "Codifying…" : "Codified data";
  return (
    <>
      <Tooltip label={found.run ? `Your codification of this ${TRIGGER_LABELS[message.trigger]?.toLowerCase() || "message"}: ${state || "…"} (only you see it)` : "Nothing has been codified for you yet"} hasArrow>
        <Button size={size} variant={variant} colorScheme={found.run && state ? STATE_COLOURS[state] || "gray" : "gray"} onClick={open}
          isDisabled={!found.loading && !found.run} leftIcon={isActive(found.codification) ? <Spinner size="xs" /> : undefined}>
          {"{ } "}{label}
        </Button>
      </Tooltip>
      <JsonViewerModal viewer={viewer} onClose={() => setViewer(null)} />
    </>
  );
};

/**
 * The contract as signed: your run for the signed line advice, the one
 * Bound will use. Shown prominently, with its contract data when valid.
 */
export const SignedContractPanel = ({ placementId, message, refreshKey }) => {
  const found = useMessageCodification({ placementId, message, refreshKey });
  const [viewer, setViewer] = useState(null);
  const names = usePartyNames(found.setting ? [found.setting.partyId] : []);
  const c = found.codification;
  const state = c?.state || (found.run?.error ? "failed" : null);
  const valid = state === "valid" && c?.contractData;
  if (!placementId || !message) return null;

  return (
    <Box borderWidth="2px" borderColor={valid ? "green.400" : "gray.200"} borderRadius="lg" bg="whiteAlpha.900" p={4}>
      <HStack justify="space-between" mb={2} flexWrap="wrap" rowGap={2}>
        <Box>
          <HStack spacing={2}>
            <Text fontWeight="bold" fontSize="lg">Contract as signed</Text>
            {state && <Badge colorScheme={STATE_COLOURS[state] || "gray"} textTransform="none">{state}</Badge>}
            {isActive(c) && <Spinner size="xs" color="blue.500" />}
          </HStack>
          <Text fontSize="sm" color="gray.600">
            Your codification of the signed line advice. This is the one Bound will use. Only you see it.
          </Text>
        </Box>
        {runData(c) && (
          <Button size="sm" variant="outline" onClick={() => setViewer(viewerFor(found, names[found.setting?.partyId]))}>Open full view</Button>
        )}
      </HStack>

      {found.loading ? (
        <Spinner size="sm" />
      ) : !found.run ? (
        <Text fontSize="sm" color="gray.500">Not codified yet. It starts when the signed line advice is opened.</Text>
      ) : isActive(c) || !c ? (
        <Box>
          <Text fontSize="sm" color="gray.600" mb={1}>Codifying… {c?.rowsTotal ? `${c.rowsDone || 0}/${c.rowsTotal} rows` : ""}</Text>
          <Progress size="xs" isIndeterminate={!c?.rowsTotal} value={c?.rowsTotal ? ((c.rowsDone || 0) / c.rowsTotal) * 100 : 0} borderRadius="full" />
        </Box>
      ) : (
        <VStack align="stretch" spacing={3}>
          {(c.error || found.run.error) && (
            <Alert status="error" borderRadius="md" py={2}><AlertIcon /><AlertDescription fontSize="sm">{c.error || found.run.error}</AlertDescription></Alert>
          )}
          {(c.validationErrors || []).length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="orange.700">Doesn't match the contract schema yet</Text>
              <UnorderedList fontSize="xs" color="orange.800">{c.validationErrors.map((e, i) => <ListItem key={i}>{e}</ListItem>)}</UnorderedList>
            </Box>
          )}
          {valid && (
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {c.contractData.schema}{c.contractData.schemaVersion ? ` v${c.contractData.schemaVersion}` : ""} · {c.specificationName || found.setting?.specificationName} · {when(found.run.startedDate)}
              </Text>
              <Box borderWidth="1px" borderRadius="md" p={3} bg="white" maxH="520px" overflowY="auto">
                <ReactJson src={parse(c.contractData.body) || {}} name={false} collapsed={2} displayDataTypes={false} displayObjectSize={false}
                  enableClipboard indentWidth={2} style={{ fontSize: "12px" }} />
              </Box>
            </Box>
          )}
        </VStack>
      )}
      <JsonViewerModal viewer={viewer} onClose={() => setViewer(null)} />
    </Box>
  );
};
